import { Container, Skeleton } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { AppBox, AppTable, AppTypography, TextWithToolTip } from 'components'
import { centsToDollar } from 'globals/functions'
import { useSubAccountsAndDiscounts } from 'hooks'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MembershipPlanViewModel } from 'sdk'
import { PaymentMethodSliceSelector } from 'store/slices/payment-methods.slice'

interface Props {
  selectedMembership: MembershipPlanViewModel | null
}

export const ChangeMembershipPurchase: FC<Props> = ({ selectedMembership }) => {
  const columns: ColumnDef<{ Item: string; No: string; Total: string }>[] = [
    {
      header: 'Item',
      accessorKey: 'Item',
    },
    {
      header: 'No. of Units',
      accessorKey: 'No',
    },
    {
      header: () => (
        <TextWithToolTip
          children={`Total/${isMonthlyPlan ? 'Mon' : 'Year'}`}
          tooltipText={'Pricing reflects base amount before being pro-rated or applying volume discounts.'}
        />
      ),
      accessorKey: 'Total',
    },
  ]

  const { coupon, isGettingAccounts, isGettingCoupons, activeBillingSubAccounts } = useSubAccountsAndDiscounts({
    applyCoupon: true,
  })
  const { DefaultPaymentMethod } = useSelector(PaymentMethodSliceSelector)

  const isMonthlyPlan = useMemo(() => {
    return selectedMembership?.Interval === 'monthly'
  }, [selectedMembership])
  const totalSeats = useMemo(() => {
    return (activeBillingSubAccounts?.length ?? 0) + 1
  }, [activeBillingSubAccounts])

  const ammountwithOutDiscount = useMemo(() => {
    return totalSeats * (selectedMembership?.Price ?? 0)
  }, [totalSeats, selectedMembership])

  const discountApplied = useMemo(() => {
    if (coupon && activeBillingSubAccounts && activeBillingSubAccounts?.length > 5) {
      if (coupon.AmountOff) {
        return coupon.AmountOff ?? 0
      } else if (coupon.PercentOff) {
        return (coupon.PercentOff / 100) * ammountwithOutDiscount
      }
    }
    return 0
  }, [coupon, ammountwithOutDiscount, activeBillingSubAccounts])
  const finalAmount = useMemo(() => {
    return ammountwithOutDiscount - discountApplied
  }, [ammountwithOutDiscount, discountApplied])
  const data = useMemo(() => {
    return {
      Item: `${selectedMembership?.Billed} - ${centsToDollar(selectedMembership?.Price ?? 0)} ${selectedMembership?.Title}`,
      No: `${totalSeats} User Seat(s)`,
      Total: centsToDollar(ammountwithOutDiscount),
    }
  }, [totalSeats, selectedMembership, ammountwithOutDiscount])

  return (
    <>
      <Container>
        <AppBox display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4}>
          <AppTypography fontSize={14} fontWeight={500} color={'grey.500'} component={'span'}>
            Please verify your order. Upon clicking the{' '}
            <AppTypography fontWeight={600} component={'span'}>
              Make Payment
            </AppTypography>{' '}
            button we’ll attempt to charge your payment method to complete the transaction.
          </AppTypography>
          <AppBox border={1} borderColor={'grey.300'} padding={4} borderRadius={6}>
            <AppTable data={[data ?? {}]} columns={columns} />
          </AppBox>
          <AppBox border={1} borderColor={'grey.300'} padding={4} borderRadius={6}>
            <AppBox display={'flex'} justifyContent={'space-between'}>
              <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                Payment Method
              </AppTypography>
              <AppBox display={'flex'} alignItems={'center'} gap={1}>
                <AppTypography fontSize={14} color={'primary.200'} fontWeight={600}>
                  {DefaultPaymentMethod?.CardType ? (
                    DefaultPaymentMethod?.CardType.toLocaleUpperCase() + ' '
                  ) : (
                    <Skeleton height={'15'} width={30} variant="text" />
                  )}
                  XXXX {DefaultPaymentMethod?.LastDigits}
                </AppTypography>
              </AppBox>
            </AppBox>
            <AppBox paddingTop={1}>
              <AppBox display={'flex'} justifyContent={'space-between'}>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  Total Amount
                </AppTypography>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  {isGettingAccounts || isGettingCoupons ? (
                    <Skeleton width={'100px'} variant="text" />
                  ) : (
                    centsToDollar(ammountwithOutDiscount)
                  )}
                </AppTypography>
              </AppBox>
              <AppBox display={'flex'} justifyContent={'space-between'}>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  Discount Applied
                </AppTypography>
                <AppTypography fontSize={14} color={'grey.500'} fontWeight={500}>
                  {isGettingAccounts || isGettingCoupons ? (
                    <Skeleton width={'100px'} variant="text" />
                  ) : (
                    centsToDollar(discountApplied)
                  )}
                </AppTypography>
              </AppBox>
            </AppBox>
            <AppBox
              display={'flex'}
              justifyContent={'space-between'}
              borderTop={1}
              borderColor={'grey.300'}
              marginTop={2}
            >
              <AppTypography fontSize={14} fontWeight={'bold'}>
                Amount to be paid now
              </AppTypography>
              <AppTypography fontSize={14} fontWeight={'bold'}>
                {isGettingAccounts || isGettingCoupons ? (
                  <Skeleton width={'100px'} variant="text" />
                ) : (
                  centsToDollar(finalAmount)
                )}
              </AppTypography>
            </AppBox>
          </AppBox>
        </AppBox>
      </Container>
    </>
  )
}
